import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import {
  ArrowDown,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Camera,
  Images,
  Mail,
  MapPin,
  Menu,
  Mountain,
  Phone,
  X,
} from 'lucide-react'
import { HeroSlideshow, type PublicHeroSlide } from '../components/HeroSlideshow'
import { Logo } from '../components/Logo'
import { useSiteData } from '../hooks/useSiteData'
import { submitInquiry } from '../lib/publicApi'
import { projectShowcaseImages } from '../lib/projectImages'
import type { Project, SiteContent } from '../types'

function useReveal() {
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    )
    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)

  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])

  return matches
}

export function PublicSite() {
  const { content, projects, loading, contentReady } = useSiteData()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [lightHeader, setLightHeader] = useState(true)
  const [headerOverHero, setHeaderOverHero] = useState(true)
  const [heroMediaBright, setHeroMediaBright] = useState(false)
  const [heroMediaReady, setHeroMediaReady] = useState(false)
  const [formState, setFormState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [selectedImage, setSelectedImage] = useState(0)
  const menuDialogRef = useRef<HTMLDivElement>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const galleryDialogRef = useRef<HTMLDivElement>(null)
  useReveal()
  const isMobile = useMediaQuery('(max-width: 840px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const heroSlides = (content as SiteContent & { heroSlides?: PublicHeroSlide[] }).heroSlides || []
  const headerUsesDarkText = !lightHeader || (headerOverHero && heroMediaBright)
  const heroReady = contentReady && heroMediaReady

  useEffect(() => {
    if (!contentReady || heroMediaReady) return
    const fallback = window.setTimeout(() => setHeroMediaReady(true), 5000)
    return () => window.clearTimeout(fallback)
  }, [contentReady, heroMediaReady])

  useEffect(() => {
    const handleScroll = () => {
      const hasScrolled = window.scrollY > 30
      const probeY = (hasScrolled ? 82 : 104) + 4
      const sectionUnderHeader = document
        .elementFromPoint(Math.round(window.innerWidth / 2), Math.min(probeY, window.innerHeight - 1))
        ?.closest('.hero, .intro, .work, .process, .craft, .about, .contact, .site-footer')
      setScrolled(hasScrolled)
      setHeaderOverHero(Boolean(sectionUnderHeader?.matches('.hero')))
      setLightHeader(Boolean(sectionUnderHeader?.matches('.hero, .process, .craft, .site-footer')))
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    if (!menuOpen) return () => {
      document.body.style.overflow = ''
    }
    const dialog = menuDialogRef.current
    const menuTrigger = menuTriggerRef.current
    const controls = dialog?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
    const first = controls?.[0]
    const last = controls?.[controls.length - 1]
    first?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
      if (event.key !== 'Tab' || !first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
      menuTrigger?.focus()
    }
  }, [menuOpen])

  useEffect(() => {
    if (!selectedProject) return
    const previousFocus = document.activeElement as HTMLElement | null
    const dialog = galleryDialogRef.current
    const controls = dialog?.querySelectorAll<HTMLElement>('button:not([disabled]):not([tabindex="-1"]), a[href]')
    const first = controls?.[0]
    const last = controls?.[controls.length - 1]
    const images = selectedProject.gallery?.length ? selectedProject.gallery : [selectedProject.image]
    document.body.style.overflow = 'hidden'
    first?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedProject(null)
      if (event.key === 'ArrowLeft') setSelectedImage((current) => (current - 1 + images.length) % images.length)
      if (event.key === 'ArrowRight') setSelectedImage((current) => (current + 1) % images.length)
      if (event.key !== 'Tab' || !first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)
      previousFocus?.focus()
    }
  }, [selectedProject])

  const featuredProjects = useMemo(
    () => projects.filter((project) => project.featured),
    [projects],
  )
  const allSamples = featuredProjects.length > 0 && featuredProjects.every((project) => project.isSample)

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const values = new FormData(form)
    if (values.get('company')) return
    setFormState('sending')
    try {
      await submitInquiry({
        name: String(values.get('name') || '').trim(),
        email: String(values.get('email') || '').trim(),
        phone: String(values.get('phone') || '').trim(),
        projectType: String(values.get('projectType') || '').trim(),
        message: String(values.get('message') || '').trim(),
        status: 'new',
      })
      form.reset()
      setFormState('sent')
    } catch {
      setFormState('error')
    }
  }

  const closeMenu = () => setMenuOpen(false)
  const openGallery = (project: Project) => {
    setSelectedImage(0)
    setSelectedProject(project)
  }

  const galleryImages = selectedProject?.gallery?.length
    ? selectedProject.gallery
    : selectedProject
      ? [selectedProject.image]
      : []

  return (
    <div className="site-shell">
      {loading && <div className="loading-line" aria-hidden="true" />}
      <a className="skip-link" href="#main">Skip to content</a>

      <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''} ${headerUsesDarkText ? 'site-header--dark-text' : ''}`}>
        <div className="site-header__inner">
          <Logo />
          <nav className="desktop-nav" aria-label="Primary navigation">
            <a href="#work">Our work</a>
            <a href="#process">Process</a>
            <a href="#about">About</a>
            <a className="nav-contact" href="#inquiry-form">Start a conversation <ArrowRight size={15} /></a>
          </nav>
          <button
            ref={menuTriggerRef}
            className={`menu-button ${headerUsesDarkText ? '' : 'menu-button--light'}`}
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu size={23} />
          </button>
        </div>
      </header>

      <div ref={menuDialogRef} className={`mobile-menu ${menuOpen ? 'mobile-menu--open' : ''}`} role="dialog" aria-modal="true" aria-label="Site navigation" aria-hidden={!menuOpen}>
        <div className="mobile-menu__top">
          <Logo light />
          <button type="button" aria-label="Close menu" onClick={closeMenu}><X size={25} /></button>
        </div>
        <nav aria-label="Mobile navigation">
          <a href="#work" onClick={closeMenu}><span>01</span> Our work</a>
          <a href="#process" onClick={closeMenu}><span>02</span> Process</a>
          <a href="#about" onClick={closeMenu}><span>03</span> About</a>
          <a href="#inquiry-form" onClick={closeMenu}><span>04</span> Contact</a>
        </nav>
        <div className="mobile-menu__footer">
          <p>{content.serviceArea}</p>
          <a href={content.instagramUrl} target="_blank" rel="noreferrer">Instagram <ArrowRight size={16} /></a>
        </div>
      </div>

      {selectedProject && (
        <div ref={galleryDialogRef} className="gallery-modal" role="dialog" aria-modal="true" aria-label={`${selectedProject.title} photo gallery`}>
          <button className="gallery-modal__backdrop" type="button" tabIndex={-1} aria-label="Close gallery" onClick={() => setSelectedProject(null)} />
          <div className="gallery-modal__panel">
            <header className="gallery-modal__header">
              <div>
                <p>{selectedProject.type} · {selectedProject.year}</p>
                <h2>{selectedProject.title}</h2>
                <span>{selectedProject.location}</span>
              </div>
              <button className="gallery-modal__close" type="button" aria-label="Close gallery" onClick={() => setSelectedProject(null)}><X size={22} /></button>
            </header>
            <div className="gallery-modal__stage">
              <img src={galleryImages[selectedImage]} alt={`${selectedProject.title}, view ${selectedImage + 1} of ${galleryImages.length}`} />
              {galleryImages.length > 1 && (
                <>
                  <button className="gallery-modal__arrow gallery-modal__arrow--prev" type="button" aria-label="Previous photo" onClick={() => setSelectedImage((selectedImage - 1 + galleryImages.length) % galleryImages.length)}><ChevronLeft size={24} /></button>
                  <button className="gallery-modal__arrow gallery-modal__arrow--next" type="button" aria-label="Next photo" onClick={() => setSelectedImage((selectedImage + 1) % galleryImages.length)}><ChevronRight size={24} /></button>
                </>
              )}
              <span className="gallery-modal__count">{String(selectedImage + 1).padStart(2, '0')} / {String(galleryImages.length).padStart(2, '0')}</span>
            </div>
            <div className="gallery-modal__thumbs" aria-label="Choose a photo">
              {galleryImages.map((image, index) => (
                <button className={index === selectedImage ? 'is-active' : ''} type="button" key={image} aria-label={`View photo ${index + 1}`} aria-current={index === selectedImage ? 'true' : undefined} onClick={() => setSelectedImage(index)}>
                  <img src={image} alt="" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main id="main">
        <section className={`hero ${heroMediaBright ? 'hero--bright-media' : ''} ${heroReady ? 'hero--ready' : 'hero--loading'}`} data-media-tone={heroMediaBright ? 'bright' : 'dark'} aria-labelledby="hero-heading" aria-busy={!heroReady}>
          {contentReady && (
            <HeroSlideshow
              slides={heroSlides}
              fallbackImage={content.heroImage}
              legacyDesktopVideo={content.heroDesktopVideo}
              legacyMobileVideo={content.heroMobileVideo}
              isMobile={isMobile}
              prefersReducedMotion={prefersReducedMotion}
              slideDurationSeconds={content.heroSlideSeconds}
              onActiveMediaBrightnessChange={setHeroMediaBright}
              onInitialMediaReady={() => setHeroMediaReady(true)}
            />
          )}
          <div className="hero__veil" />
          <div className="hero__grain" />
          <div className="hero__loading-backdrop" aria-hidden="true" />
          <div className="hero__content page-width">
            <div className="hero__copy-panel">
              <div className="hero__copy-glass" aria-hidden="true" />
              <p className="eyebrow eyebrow--light hero__eyebrow"><span />{content.heroKicker}</p>
              <h1 id="hero-heading">
                <span>{content.heroTitle}</span>
                <em>{content.heroAccent}</em>
              </h1>
              <div className="hero__statement">
                <p>{content.heroBody}</p>
                <div className="hero__services" aria-label="Services">
                  <span>Site-led design</span>
                  <span>Enduring materials</span>
                  <span>Close oversight</span>
                </div>
              </div>
              <div className="hero__invitation">
                <span>A considered path from land to home.</span>
                <a className="hero__cta" href="#inquiry-form">
                  <span>Discuss your project</span>
                  <i aria-hidden="true"><ArrowRight size={18} /></i>
                </a>
              </div>
            </div>
          </div>
          <div className="hero__rail page-width">
            <a className="hero__scroll" href="#work" aria-label="Explore selected work">
              <span className="hero__scroll-label hero__scroll-label--full">Explore selected work</span>
              <span className="hero__scroll-label hero__scroll-label--compact">Explore work</span>
              <ArrowDown size={16} />
            </a>
            <div className="hero__location">
              <span>
                <Mountain size={14} />
                <span className="hero__location-label hero__location-label--full">Western North Carolina</span>
                <span className="hero__location-label hero__location-label--compact">WNC</span>
              </span>
            </div>
          </div>
        </section>

        <section className="intro section-pad" id="intro">
          <div className="page-width intro__grid">
            <p className="section-index" data-reveal>01 / Philosophy</p>
            <div className="intro__copy">
              <h2 data-reveal>{content.statementTitle}</h2>
              <div className="intro__body" data-reveal>
                <p>{content.statementBody}</p>
                <a className="text-link" href="#process">How we work <ArrowRight size={16} /></a>
              </div>
            </div>
          </div>
          <div className="page-width intro__capabilities" data-reveal>
            <article>
              <span>01</span>
              <h3>Custom residences</h3>
              <p>A focused, collaborative path from land evaluation and planning through the final details of a one-of-one home.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Thoughtful spec homes</h3>
              <p>Move-in-ready residences developed with the same discipline, material care, and connection to place.</p>
            </article>
          </div>
        </section>

        <section className="work section-pad" id="work">
          <div className="page-width">
            <div className="section-heading" data-reveal>
              <div>
                <p className="eyebrow"><span />{content.workKicker}</p>
                <h2>{allSamples ? content.workTitle : 'Selected work'}</h2>
              </div>
              <p>{allSamples ? 'A glimpse of the material palette and living experience we build toward.' : 'A selection of homes shaped around their setting and the people who live there.'}</p>
            </div>

            <div className="project-showcase">
              {featuredProjects.map((project, index) => {
                const [primaryImage, secondaryImage] = projectShowcaseImages(project, index)
                return (
                <article className={`project-feature project-feature--${index + 1}`} key={project.id} data-reveal>
                  <button className="project-feature__media" type="button" onClick={() => openGallery(project)} aria-label={`Open photo gallery for ${project.title}`}>
                    <span className="project-feature__primary">
                      <img src={primaryImage} alt={`${project.title} exterior`} loading={index === 0 ? 'eager' : 'lazy'} />
                    </span>
                    {secondaryImage && (
                      <span className="project-feature__secondary" aria-hidden="true">
                        <img src={secondaryImage} alt="" loading="lazy" />
                      </span>
                    )}
                    <span className="project-feature__view"><Images size={16} /> View gallery</span>
                  </button>
                  <div className="project-feature__content">
                    <div className="project-feature__topline">
                      <span>0{index + 1}</span>
                      <p>{project.type} · {project.year}</p>
                    </div>
                    <h3>{project.title}</h3>
                    <p className="project-feature__location"><MapPin size={14} /> {project.location}</p>
                    <p className="project-feature__summary">{project.summary}</p>
                    {!!project.highlights?.length && (
                      <ul className="project-feature__facts" aria-label="Residence details">
                        {project.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                      </ul>
                    )}
                    <button className="text-link project-feature__button" type="button" onClick={() => openGallery(project)}>Explore the residence <ArrowRight size={16} /></button>
                  </div>
                </article>
                )
              })}
            </div>
          </div>
        </section>

        <section className="process section-pad" id="process">
          <div className="page-width">
            <div className="process__header">
              <p className="section-index" data-reveal>02 / The process</p>
              <h2 data-reveal>{content.processTitle}</h2>
              <p data-reveal>A considered home deserves a considered process. We keep it personal, transparent, and focused from the first site walk to the final handoff.</p>
            </div>
            <div className="process__steps">
              {[
                ['01', 'Listen & evaluate', 'We begin with your vision, the property, and the practical parameters that will shape the project.'],
                ['02', 'Plan with purpose', 'Scope, budget, design, and schedule are aligned before construction begins.'],
                ['03', 'Build with precision', 'Close oversight and trusted trade partners keep quality and communication consistent.'],
                ['04', 'Finish with care', 'We refine every detail, complete a thorough walkthrough, and stand behind the finished home.'],
              ].map(([number, title, body]) => (
                <article className="process-step" key={number} data-reveal>
                  <span>{number}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="craft">
          <div className="craft__image craft__image--large" data-reveal>
            <img src="/images/projects/donna/06-kitchen.webp" alt="Quartz kitchen and oversized island at 386 Donna Lane" loading="lazy" />
          </div>
          <div className="craft__quote" data-reveal>
            <p>“Good building is equal parts restraint, rigor, and respect for the land.”</p>
            <span>Our approach to craft</span>
          </div>
          <div className="craft__image craft__image--small" data-reveal>
            <img src={content.craftImage} alt="BlackRock Developers craftsmanship and interior detail" loading="lazy" />
          </div>
        </section>

        <section className="about section-pad" id="about">
          <div className="page-width about__grid">
            <div className="about__image" data-reveal>
              <img src={content.aboutImage} alt="Blue Ridge Mountains in Western North Carolina" loading="lazy" />
              <div className="about__monogram"><Logo /></div>
            </div>
            <div className="about__content">
              <p className="eyebrow" data-reveal><span />{content.aboutKicker}</p>
              <h2 data-reveal>{content.aboutTitle}</h2>
              <p data-reveal>{content.aboutBody}</p>
              <div className="about__values" data-reveal>
                <span><Check size={15} /> Thoughtful planning</span>
                <span><Check size={15} /> Hands-on oversight</span>
                <span><Check size={15} /> Enduring materials</span>
              </div>
              <p className="about__area" data-reveal><Mountain size={18} /> {content.serviceArea}</p>
            </div>
          </div>
        </section>

        <section className="contact section-pad" id="contact">
          <div className="page-width contact__grid">
            <div className="contact__intro">
              <p className="section-index" data-reveal>03 / Start a conversation</p>
              <h2 data-reveal>{content.contactTitle}</h2>
              <p data-reveal>{content.contactBody}</p>
              <div className="contact__details" data-reveal>
                {content.contactEmail && <a href={`mailto:${content.contactEmail}`}><Mail size={17} /> {content.contactEmail}</a>}
                {content.contactPhone && <a href={`tel:${content.contactPhone.replace(/\D/g, '')}`}><Phone size={17} /> {content.contactPhone}</a>}
                <a href={content.instagramUrl} target="_blank" rel="noreferrer"><Camera size={17} /> @blackrock.developers.nc</a>
              </div>
            </div>

            <form className="contact-form" id="inquiry-form" onSubmit={submitLead} data-reveal>
              <div className="form-row">
                <label>
                  <span>Your name *</span>
                  <input name="name" type="text" autoComplete="name" required minLength={2} placeholder="First and last name" />
                </label>
                <label>
                  <span>Email address *</span>
                  <input name="email" type="email" autoComplete="email" required placeholder="you@example.com" />
                </label>
              </div>
              <div className="form-row">
                <label>
                  <span>Phone</span>
                  <input name="phone" type="tel" autoComplete="tel" placeholder="(828) 000-0000" />
                </label>
                <label>
                  <span>I’m interested in</span>
                  <select name="projectType" defaultValue="Custom home">
                    <option>Custom home</option>
                    <option>Available spec home</option>
                    <option>Land evaluation</option>
                    <option>Partnership</option>
                    <option>Something else</option>
                  </select>
                </label>
              </div>
              <label>
                <span>Tell us about your project *</span>
                <textarea name="message" required minLength={10} rows={5} placeholder="Location, timing, style, or anything else you’d like us to know." />
              </label>
              <label className="form-trap" aria-hidden="true">
                Company<input name="company" type="text" tabIndex={-1} autoComplete="off" />
              </label>
              <label className="contact-form__consent">
                <input name="legalConsent" type="checkbox" required />
                <span>I have read the <a href="/privacy">Privacy Policy</a>, agree to the <a href="/terms">Terms of Service</a>, and authorize BlackRock Developers to use this information to respond to my inquiry.</span>
              </label>
              <div className="contact-form__footer">
                <p>We typically reply within two business days.</p>
                <button className="button button--dark" type="submit" disabled={formState === 'sending' || formState === 'sent'}>
                  {formState === 'sending' && 'Sending…'}
                  {formState === 'sent' && <>Message received <Check size={17} /></>}
                  {(formState === 'idle' || formState === 'error') && <>Send inquiry <ArrowRight size={17} /></>}
                </button>
              </div>
              {formState === 'error' && <p className="form-error" role="alert">We couldn’t send that just now. Please try again or reach out on Instagram.</p>}
              <p className="sr-only" aria-live="polite">
                {formState === 'sending' && 'Sending your inquiry.'}
                {formState === 'sent' && 'Your inquiry was received successfully.'}
              </p>
            </form>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="page-width">
          <div className="site-footer__top">
            <Logo light />
            <p>Creating enduring residences across Western North Carolina.</p>
          </div>
          <div className="site-footer__links">
            <div>
              <span>Explore</span>
              <a href="#work">Our work</a>
              <a href="#process">Process</a>
              <a href="#about">About</a>
            </div>
            <div>
              <span>Connect</span>
              <a href={content.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>
              <a href="#inquiry-form">Start a project</a>
              {content.contactEmail && <a href={`mailto:${content.contactEmail}`}>Email us</a>}
            </div>
            <div className="site-footer__region">
              <span>Based in</span>
              <p>Western North Carolina<br />United States</p>
            </div>
          </div>
          <div className="site-footer__bottom">
            <p>© {new Date().getFullYear()} BlackRock Developers. All rights reserved.</p>
            <div className="site-footer__credits">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Service</a>
              <a href="https://commons.wikimedia.org/wiki/File:Frying_pan_mountain_-2_(49281956256).jpg" target="_blank" rel="noreferrer">Blue Ridge photograph: Olaf Zerbock · CC BY 2.0</a>
              <a href="/admin">Site administration</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
